/** Review with Iris: every storyboard check goes in one of three buckets.
 * must — red, blocks continuing; look — yellow, the maker's eye, never blocks; iris — Iris approves it herself
 * with one sentence. Pure: the studio passes its review model, the scenes and the maker's stored decisions. */

export type Bucket = "must" | "look" | "iris";

export interface ReviewReason {
  kind: string;
  message: string;
  confidence?: number;
  severity?: string;
  finding?: { severity?: string; summary?: string; id?: string; [key: string]: unknown };
  patch?: { rationale?: string; [key: string]: unknown };
  background?: { review_level?: string; [key: string]: unknown };
  [key: string]: unknown;
}

export interface ReviewScene {
  id: string;
  position?: number;
  image_asset_id?: string | null;
  picture_change?: string;
  stale_badges?: string[];
  continuity_stale?: boolean;
  director?: { narrative_beat?: string; [key: string]: unknown } | null;
  [key: string]: unknown;
}

export interface ReviewItem {
  sceneId: string;
  position: number;
  beat: string;
  imageUrl: string;
  reasons: (ReviewReason & { bucket: Bucket; sentence: string })[];
  signature: string;
  bucket: Bucket;
  decision: string | null;
  /** An outdated picture the maker may simply confirm (accept-continuity, 0 credits). */
  acceptable: boolean;
}

export interface Decision { decision: string; signature: string }

export interface IrisReviewResult {
  must: ReviewItem[];
  look: ReviewItem[];
  iris: ReviewItem[];
  done: ReviewItem[];
  counts: { must: number; look: number; iris: number; done: number; later: number };
}

const REVIEWABLE_BADGES = ["contract stale", "vehicle/prop reference stale"];
const ORDER: Record<Bucket, number> = { iris: 0, look: 1, must: 2 };

export function bucketOf(reason: ReviewReason, scene: ReviewScene): Bucket {
  switch (reason.kind) {
    case "question":
    case "content":
    case "conflict":
      return "must";
    case "finding":
      return reason.finding?.severity === "high" ? "must" : "iris";
    case "contract":
      return reason.severity === "block" ? "must" : "iris";
    case "background_continuity":
      return reason.background?.review_level === "attention" ? "must" : "look";
    case "assumption":
      return Number(reason.confidence ?? reason.patch?.confidence ?? 0) >= 0.75 ? "iris" : "look";
    case "continuity":
      // Iris approves it herself only when nothing but unseen contract details changed: a new costume or face is seen.
      return scene.picture_change === "invisible" && (scene.stale_badges || []).every((badge) => badge === "contract stale")
        ? "iris"
        : "look";
    default:
      return "look";
  }
}

export function irisSentence(reason: ReviewReason, scene: ReviewScene): string {
  if (bucketOf(reason, scene) !== "iris") return reason.message;
  switch (reason.kind) {
    case "assumption":
      return `Story assumption with high confidence: ${reason.patch?.rationale || reason.message}`;
    case "finding":
      return `Story note, not a mistake: ${reason.finding?.summary || reason.message}`;
    case "contract":
      return `Small contract note: ${reason.message}`;
    case "continuity":
      return "Only unseen details changed since the picture (timing, lyrics, motifs).";
    default:
      return reason.message;
  }
}

function signatureOf(reasons: (ReviewReason & { bucket: Bucket })[], scene: ReviewScene): string {
  const parts = reasons.filter((reason) => reason.bucket !== "iris").map((reason) => `${reason.kind}:${reason.message}`);
  // The outdated-picture note is always the same sentence; its contract version and badges make a later change lapse it.
  if (parts.some((part) => part.startsWith("continuity:"))) {
    const contract = (scene.scene_contract || {}) as { version?: number };
    parts.push(`contract:${Number(contract.version || 0)}:${[...(scene.stale_badges || [])].sort().join(",")}`);
  }
  return parts.sort().join("|");
}

export function irisReview(
  scenes: ReviewScene[],
  review: { bySceneId: Map<string, { reasons?: ReviewReason[]; questions?: ReviewReason[] }> },
  decisions: Record<string, Decision | undefined>,
  imageUrl: (scene: ReviewScene) => string,
): IrisReviewResult {
  const result: IrisReviewResult = { must: [], look: [], iris: [], done: [], counts: { must: 0, look: 0, iris: 0, done: 0, later: 0 } };
  const ordered = [...scenes].sort((a, b) => Number(a.position || 0) - Number(b.position || 0));
  for (const scene of ordered) {
    const state = review.bySceneId.get(String(scene.id));
    const raw = [...(state?.questions || []).map((question) => ({ ...question, kind: "question", message: String(question.message ?? question.prompt ?? "") })), ...(state?.reasons || [])];
    if (!raw.length) continue;
    const reasons = raw.map((reason) => ({ ...reason, bucket: bucketOf(reason, scene), sentence: irisSentence(reason, scene) }));
    const bucket = reasons.reduce<Bucket>((top, reason) => (ORDER[reason.bucket] > ORDER[top] ? reason.bucket : top), "iris");
    const signature = signatureOf(reasons, scene);
    const stored = decisions[String(scene.id)];
    const [storedSignature, replaced] = String(stored?.signature || "").split("#");
    const matches = !!stored && storedSignature === signature;
    const item: ReviewItem = {
      sceneId: String(scene.id),
      position: Number(scene.position || 0),
      beat: String(scene.director?.narrative_beat || scene.visual_description || ""),
      imageUrl: scene.image_asset_id ? imageUrl(scene) : "",
      reasons,
      signature,
      bucket,
      decision: matches ? stored!.decision : null,
      acceptable: !!scene.image_asset_id && !!scene.continuity_stale
        && (scene.stale_badges || []).length > 0
        && (scene.stale_badges || []).every((badge) => REVIEWABLE_BADGES.includes(badge)),
    };
    if (bucket === "must") {
      result.must.push(item);
    } else if (matches && (stored!.decision === "fine" || stored!.decision === "later")) {
      result.done.push(item);
      if (stored!.decision === "later") result.counts.later += 1;
    } else if (matches && stored!.decision === "remake" && replaced && scene.image_asset_id && String(scene.image_asset_id) !== replaced) {
      result.done.push(item);
    } else if (bucket === "iris" && !(matches && stored!.decision === "look")) {
      result.iris.push(item);
    } else {
      result.look.push(item);
    }
  }
  result.counts.must = result.must.length;
  result.counts.look = result.look.length;
  result.counts.iris = result.iris.length;
  result.counts.done = result.done.length;
  return result;
}
